import { z } from "zod";

export const optionSchema = z.object({ value: z.string(), label: z.string().trim().min(1) });
export const questionSchema = z.object({
  key: z.string(),
  code: z.string(),
  position: z.number(),
  type: z.enum(["TEXT", "SINGLE_CHOICE", "RATING_SCALE"]),
  prompt: z.string(),
  required: z.boolean(),
  allowOther: z.boolean().default(false),
  options: z.array(optionSchema),
  maxLength: z.number().nullable().optional(),
  scaleMin: z.number().nullable().optional(),
  scaleMax: z.number().nullable().optional(),
  scaleMinLabel: z.string().nullable().optional(),
  scaleMaxLabel: z.string().nullable().optional(),
});
export const draftQuestionSchema = z.object({
  key: z.string().optional(),
  type: z.enum(["TEXT", "SINGLE_CHOICE", "RATING_SCALE"]),
  prompt: z.string().trim().min(1),
  required: z.boolean(),
  allowOther: z.boolean().default(false),
  options: z.array(optionSchema),
  maxLength: z.number().nullable().optional(),
  scaleMin: z.number().nullable().optional(),
  scaleMax: z.number().nullable().optional(),
  scaleMinLabel: z.string().nullable().optional(),
  scaleMaxLabel: z.string().nullable().optional(),
});
export const availabilitySchema = z.object({
  status: z.enum(["OPEN", "CLOSED"]),
  opensAt: z.string().datetime().nullable().optional(),
  closesAt: z.string().datetime().nullable().optional(),
});
export const questionnaireSchema = z.object({
  key: z.string(),
  title: z.string(),
  versionId: z.string(),
  versionNumber: z.number(),
  definitionDigest: z.string(),
  availability: availabilitySchema,
  namedResponseNotice: z.string(),
  questions: z.array(questionSchema),
  rowVersion: z.number(),
});
export const questionnaireDraftSchema = z.object({
  title: z.string(),
  namedResponseNotice: z.string(),
  questions: z.array(questionSchema),
  rowVersion: z.number(),
  lastPublishedVersionNumber: z.number().nullable().optional(),
});
export const questionnaireVersionSummarySchema = z.object({
  id: z.string(),
  versionNumber: z.number(),
  title: z.string(),
  publishedAt: z.string(),
});
export const questionnaireVersionSchema = z.object({
  id: z.string(),
  key: z.string(),
  title: z.string(),
  versionNumber: z.number(),
  definitionDigest: z.string(),
  namedResponseNotice: z.string(),
  questions: z.array(questionSchema),
  publishedAt: z.string(),
});
export type Questionnaire = z.infer<typeof questionnaireSchema>;
export type QuestionnaireAvailability = z.infer<typeof availabilitySchema>;
export type DraftQuestion = z.infer<typeof draftQuestionSchema>;
export type QuestionnaireDraft = z.infer<typeof questionnaireDraftSchema>;
export type QuestionnaireVersionSummary = z.infer<typeof questionnaireVersionSummarySchema>;

export const submissionAnswerInputSchema = z.object({
  questionKey: z.string(),
  textValue: z.string().trim().optional(),
  selectedValue: z.string().optional(),
  ratingValue: z.number().int().optional(),
  otherText: z.string().trim().optional(),
});
export type SubmissionAnswerInput = z.infer<typeof submissionAnswerInputSchema>;

export const receiptSchema = z.object({
  id: z.string(),
  status: z.literal("COMPLETED"),
  submittedAt: z.string(),
  versionId: z.string(),
});
const memberResultSchema = z.object({
  id: z.string(),
  username: z.string(),
  email: z.string(),
  role: z.string(),
  status: z.string(),
  registrantName: z.string(),
  companyName: z.string().nullable().optional(),
  rowVersion: z.number(),
});
export const questionnaireMemberStatusSchema = z.object({
  member: memberResultSchema,
  submissionStatus: z.enum(["COMPLETED", "NOT_SUBMITTED"]),
  submissionId: z.string().nullable(),
  submittedAt: z.string().nullable(),
});
export const questionnaireMemberStatusPageSchema = z.object({
  items: z.array(questionnaireMemberStatusSchema),
  nextCursor: z.string().nullable().optional(),
});
export const submissionItemSchema = z.object({
  id: z.string(),
  member: memberResultSchema,
  submittedAt: z.string(),
  versionId: z.string(),
  versionNumber: z.number(),
});
export const submissionPageSchema = z.object({
  items: z.array(submissionItemSchema),
  nextCursor: z.string().nullable().optional(),
});
export const submissionAnswerSchema = z.object({
  questionKey: z.string(),
  questionCode: z.string(),
  prompt: z.string(),
  type: z.enum(["TEXT", "SINGLE_CHOICE", "RATING_SCALE"]),
  textValue: z.string().nullable().optional(),
  selectedValue: z.string().nullable().optional(),
  selectedLabel: z.string().nullable().optional(),
  ratingValue: z.number().nullable().optional(),
  otherText: z.string().nullable().optional(),
});
export const submissionDetailSchema = z.object({
  id: z.string(),
  questionnaireKey: z.string(),
  versionId: z.string(),
  versionNumber: z.number(),
  versionTitle: z.string(),
  member: memberResultSchema,
  status: z.string(),
  submittedAt: z.string(),
  answers: z.array(submissionAnswerSchema),
});
export type SubmissionDetail = z.infer<typeof submissionDetailSchema>;

export const summarySchema = z.object({
  questionnaireKey: z.string(),
  versionId: z.string(),
  versionNumber: z.number(),
  completedCount: z.number(),
  distributions: z.array(
    z.object({
      questionKey: z.string(),
      questionCode: z.string(),
      prompt: z.string(),
      type: z.enum(["SINGLE_CHOICE", "RATING_SCALE"]),
      answeredCount: z.number(),
      unansweredCount: z.number(),
      buckets: z.array(
        z.object({
          value: z.union([z.string(), z.number()]),
          label: z.string(),
          count: z.number(),
        }),
      ),
    }),
  ),
});
